import React, { useMemo } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { TZDate } from "@date-fns/tz";
import { queryClient } from "@shared/api/query-client";
import { Button } from "@shared/ui/button/button";
import { AppointmentNote } from "../appointment-note/appointment-note";
import { HomeworkCard } from "../homework-card/homework-card";
import { itemVariants, listVariants } from "@widgets/appointment/lib/appointment-animation";
import s from "./past-lessons-feed.module.scss";

/**
 * Лента прошлых занятий: подгружается прямо в общий список материалов
 * (кнопкой «Загрузить материалы прошлых занятий»). Пункты всех занятий
 * склеены в единую хронологическую ленту; разделение по дням — как в чате:
 * липкая центрированная метка «12 мая» между блоками дня.
 */
export const PastLessonsFeed = ({
	pastLessons,
	hasMore,
	isFetchingPast,
	onLoadMore,
	kind,
	appointment,
	isTeacher,
}) => {
	const profileData = queryClient.getQueryData(["profile"]);
	const profile = profileData?.data || profileData;
	const timezone = profile?.timezone;

	// Плоская лента пунктов с контекстом занятия (время, инструмент)
	const items = useMemo(() => {
		const list = [];
		pastLessons?.forEach((lesson) => {
			const lessonItems = kind === "notes" ? lesson?.notes : lesson?.homework;
			lessonItems?.forEach((item) => {
				list.push({ item, lesson });
			});
		});
		return list;
	}, [pastLessons, kind]);

	// Группировка по дню занятия (в таймзоне профиля) — как groupMessagesByDate в чате
	const groups = useMemo(() => {
		const result = [];
		let currentKey = null;
		items.forEach(({ item, lesson }) => {
			const day = lesson?.starts_at
				? new TZDate(lesson.starts_at, timezone)
				: null;
			const validDay = day && !Number.isNaN(day.getTime());
			const key = validDay ? format(day, "yyyy-MM-dd") : "unknown";
			const timeLabel = validDay ? format(day, "HH:mm") : "";
			if (key !== currentKey) {
				currentKey = key;
				result.push({
					key,
					label: validDay
						? format(day, "d MMMM", { locale: ru })
						: "Дата неизвестна",
					entries: [],
				});
			}
			result[result.length - 1].entries.push({ item, lesson, timeLabel });
		});
		return result;
	}, [items, timezone]);

	if (!pastLessons?.length && !hasMore) return null;

	return (
		<motion.div
			key={`past-lessons-${kind}`}
			initial="hidden"
			animate="visible"
			variants={listVariants}
			style={{ width: "100%" }}
		>
			{!!pastLessons?.length && (
				<motion.div variants={itemVariants} className={s.divider}>
					<span className={s.dividerText}>Материалы прошлых занятий</span>
				</motion.div>
			)}

			{groups.map((group) => (
				<motion.section
					key={group.key}
					variants={itemVariants}
					className={s.dayGroup}
					style={{ width: "100%" }}
				>
					{/* липкая метка дня — как в чате */}
					<div className={s.dayLabel}>
						<span className={s.dayText}>{group.label}</span>
					</div>
					{group.entries.map(({ item, lesson, timeLabel }, index) => {
						const lessonId = lesson?.id;
						const instrumentName = lesson?.instrument?.name;
						const caption = [timeLabel, instrumentName].filter(Boolean).join(" · ");

						return (
							<div key={`past-${kind}-${item?.id || index}`} className={s.entry}>
								{caption ? <span className={s.entryCaption}>{caption}</span> : null}
								{kind === "notes" ? (
									<AppointmentNote
										{...{
											note: item,
											appointmentId: lessonId,
											isTeacher,
											showAuthorInfo: true,
										}}
									/>
								) : (
									<HomeworkCard
										{...{
											homework: item,
											appointmentId: lessonId,
											appointment,
											isTeacher,
										}}
									/>
								)}
							</div>
						);
					})}
				</motion.section>
			))}

			{hasMore ? (
				<motion.div variants={itemVariants} className={s.loadMoreWrapper}>
					<Button
						theme="simple"
						size="small"
						fetching={isFetchingPast}
						label="Загрузить материалы прошлых занятий"
						onClick={onLoadMore}
						className={s.loadMoreButton}
					/>
				</motion.div>
			) : null}
		</motion.div>
	);
};
